import React, { createContext, useContext, useMemo, ReactNode } from 'react';

export interface PrincipalIdentity {
  principalId: string;
  nodeId: string;
  displayName: string;
  hxnAlias?: string;
  hnsAlias?: string;
  avatarIcon?: string;
  isOwner?: boolean;
}

export interface PrincipalContextValue extends PrincipalIdentity {
  isSelf: (authorPrincipalId?: string, authorDisplayName?: string, authorId?: string) => boolean;
}

const defaultSelfCheck = (authorPrincipalId?: string, authorDisplayName?: string, authorId?: string): boolean => {
  if (authorPrincipalId === 'self' || authorId === 'self') return true;
  return false;
};

const PrincipalContext = createContext<PrincipalContextValue>({
  principalId: 'self',
  nodeId: 'self',
  displayName: 'You',
  avatarIcon: '👤',
  isSelf: defaultSelfCheck,
});

export const useCurrentPrincipal = (): PrincipalContextValue => {
  return useContext(PrincipalContext);
};

export const PrincipalProvider: React.FC<{ identity: PrincipalIdentity; children: ReactNode }> = ({ identity, children }) => {
  const value = useMemo<PrincipalContextValue>(() => {
    return {
      ...identity,
      isSelf: (authorPrincipalId?: string, authorDisplayName?: string, authorId?: string): boolean => {
        if (authorPrincipalId && authorPrincipalId === identity.principalId) return true;
        if (authorId && (authorId === identity.nodeId || (authorId === 'user' && identity.isOwner))) return true;
        if (authorPrincipalId && authorPrincipalId === identity.nodeId) return true;
        if (
          authorDisplayName &&
          identity.displayName &&
          authorDisplayName.toLowerCase().trim() === identity.displayName.toLowerCase().trim() &&
          authorDisplayName !== 'You' &&
          authorDisplayName !== 'User'
        ) {
          return true;
        }
        if (authorPrincipalId === 'self' || authorId === 'self') return true;
        return false;
      },
    };
  }, [identity]);

  return <PrincipalContext.Provider value={value}>{children}</PrincipalContext.Provider>;
};
